import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';

// Backend routes live under /admin/topup-exam-board; axiosInstance already prefixes /api
const API_BASE = '/admin/topup-exam-board';

const statusBadges = {
  current: 'bg-green-100 text-green-800 border border-green-200',
  future: 'bg-blue-100 text-blue-800 border border-blue-200',
  completed: 'bg-gray-100 text-gray-700 border border-gray-200',
};

const DOC_SUBJECTS = [
  {
    id: 'AD',
    label: 'AD',
    coursework: [
      { id: 'CW1', label: 'CW1', templates: ['_AD_CW1', '_AD_CW1_GR', '_AD_CW1_Presentation'] },
      { id: 'CW2', label: 'CW2', templates: ['_AD_CW2', '_AD_CW2_GR', '_AD_CW2_Presentation'] },
      { id: 'Exam', label: 'Exam', templates: ['_AD_WrittenExam'] },
    ],
  },
  {
    id: 'ASE',
    label: 'ASE',
    coursework: [
      { id: 'CW1', label: 'CW1', templates: ['_ASE_CW1', '_ASE_CW1_GR', '_ASE_CW1_Presentation'] },
      { id: 'CW2', label: 'CW2', templates: ['_ASE_CW2', '_ASE_CW2_GR', '_ASE_CW2_Presentation'] },
      { id: 'Exam', label: 'Exam', templates: ['_ASE_WrittenExam'] },
    ],
  },
  {
    id: 'AI',
    label: 'AI',
    coursework: [
      { id: 'CW1', label: 'CW1', templates: ['_AI_CW1', '_AI_CW1_GR', '_AI_CW1_Presentation'] },
      { id: 'CW2', label: 'CW2', templates: ['_AI_CW2', '_AI_CW2_GR', '_AI_CW2_Presentation'] },
    ],
  },
  {
    id: 'PAP',
    label: 'PAP',
    coursework: [
      { id: 'CW', label: 'CW', templates: ['_PAP_CW', '_PAP_CW_Report', '_PAP_CW_GR'] },
    ],
  },
  {
    id: 'Project',
    label: 'Project',
    coursework: [
      { id: 'Project', label: 'Project', templates: ['_FP_InterimReport', '_FP_Report', '_FP_Presentation', '_FP_GR'] },
    ],
  },
];

// Convert a limited set of Roman numerals to integers for ordering (I to X covers typical subject numbering)
function romanToInt(roman) {
  if (!roman) return null;
  const map = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let total = 0;
  let prev = 0;
  const upper = roman.toUpperCase();
  for (let i = upper.length - 1; i >= 0; i -= 1) {
    const curr = map[upper[i]] || 0;
    if (curr < prev) total -= curr; else total += curr;
    prev = curr;
  }
  return total || null;
}

// Build ordering keys so subjects like "AD - 1" and "AD - 2" stay adjacent
function buildSubjectOrderKey(subj) {
  const raw = (subj.shortName || subj.fullName || '').trim();
  if (!raw) return { base: '', num: null, display: '' };

  // Normalize separators for easier matching
  const normalized = raw.replace(/\s+/g, ' ').replace(/\s*-\s*/g, '-');
  const match = normalized.match(/^(.*?)[-\s]?([0-9]+|[IVXLCDM]+)$/i);

  if (!match) {
    return { base: normalized.toUpperCase(), num: null, display: normalized.toUpperCase() };
  }

  const base = (match[1] || '').trim().toUpperCase();
  const suffix = match[2] || '';
  const num = /^[0-9]+$/.test(suffix) ? Number(suffix) : romanToInt(suffix);
  return {
    base,
    num: Number.isNaN(num) ? null : num,
    display: normalized.toUpperCase(),
  };
}

// Custom priority ordering for certain subject names
function subjectPriority(subj) {
  const raw = (subj.shortName || subj.fullName || '').toLowerCase();
  const priorities = ['pro', 'int', 'viva', 'proc', 'fin'];

  // Tokenize to avoid matching "pro" inside "project"
  const tokens = raw
    .replace(/\s*[-–—]\s*/g, ' ') // normalize dashes
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

  const last = tokens[tokens.length - 1] || '';
  const exactIdx = priorities.indexOf(last);
  if (exactIdx !== -1) return exactIdx;

  for (let i = 0; i < priorities.length; i += 1) {
    if (tokens.includes(priorities[i])) return i;
  }

  return null;
}

const submissionStatusOptions = [
  'on-time submission',
  'late submission',
  're-submission',
  'not submitted',
];

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="260" viewBox="0 0 200 260"><rect width="200" height="260" fill="%23f1f5f9"/><text x="50%" y="50%" text-anchor="middle" fill="%2394a3b8" font-size="20" font-family="Arial, sans-serif">No Photo</text></svg>';

const PassportWrapper = styled.div`
  width: 150px;
  height: 120px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const PassportImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  display: block;
`;

const SmallPassportWrapper = styled(PassportWrapper)`
  width: 80px;
  height: 80px;
`;

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function buildOptions(items) {
  return items.map((board) => ({
    value: board.examBoardId,
    label: `${board.examBoardName} • ${formatDate(board.date)} (${board.status})`,
  }));
}

function StudentAvatar({ name }) {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 font-semibold">
      {initials || '?'}
    </div>
  );
}

function getSubjectsForGroup(students, courseTitle) {
  const map = new Map();
  students.forEach((s) => {
    (s.batchSubjects || []).forEach((bs) => {
      if (bs.subject && !map.has(bs.subject.subjectId)) {
        map.set(bs.subject.subjectId, {
          subjectId: bs.subject.subjectId,
          shortName: bs.subject.shortName,
          fullName: bs.subject.fullName,
          semester: bs.semester || '0',
        });
      }
    });
  });

  return Array.from(map.values()).sort((a, b) => {
    const pa = subjectPriority(a);
    const pb = subjectPriority(b);
    if (pa !== null || pb !== null) {
      // Place priority subjects (Pro, Int, Viva, Proc, Fin) after non-priority ones
      if (pa !== null && pb === null) return 1;
      if (pa === null && pb !== null) return -1;
      if (pa !== null && pb !== null && pa !== pb) return pa - pb;
    }

    const ka = buildSubjectOrderKey(a);
    const kb = buildSubjectOrderKey(b);

    if (ka.base !== kb.base) return ka.base.localeCompare(kb.base);
    if (ka.num !== null && kb.num !== null && ka.num !== kb.num) return ka.num - kb.num;

    const sa = parseInt(a.semester, 10) || 0;
    const sb = parseInt(b.semester, 10) || 0;
    if (sa !== sb) return sa - sb;

    return ka.display.localeCompare(kb.display);
  });
}

function getMarksDisplayWithAttempt(submission, showSheetMarks) {
  if (!submission) {
    return {
      displayValue: null,
      variant: 'not-submitted',
      indicator: null,
      badgeClass: 'bg-slate-100 text-slate-600 border border-slate-200',
      indicatorClass: '',
      label: 'NS',
    };
  }

  const first = submission.marks;
  const second = submission.second_marks;
  const status = submission.submission_status;
  const isNotSubmitted = status === 'not submitted';
  const isSubmittedNoMarks = !isNotSubmitted && (first === null || first === undefined) && (second === null || second === undefined);

  let displayValue = null;
  let variant = 'neutral';
  let indicator = null;
  let label = 'NS';

  if (isNotSubmitted) {
    variant = 'not-submitted';
    label = 'NS';
  } else if (isSubmittedNoMarks) {
    variant = 'submitted-no-marks';
    label = 'SN';
  } else if (first !== null && !Number.isNaN(first) && first >= 40) {
    displayValue = first;
    variant = 'pass';
    indicator = '1';
    label = String(first);
  } else if (second !== null && !Number.isNaN(second) && second >= 40) {
    displayValue = showSheetMarks ? Math.min(second, 43) : second;
    variant = 'warning';
    indicator = '2';
    label = String(displayValue);
  } else if (first !== null && !Number.isNaN(first)) {
    displayValue = first;
    variant = 'danger';
    indicator = second !== null && !Number.isNaN(second) && second < 40 ? 'F' : 'R';
    label = String(first);
  } else if (second !== null && !Number.isNaN(second)) {
    displayValue = second;
    variant = 'danger';
    indicator = 'R';
    label = String(second);
  }

  const badgeClass = variant === 'pass'
    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
    : variant === 'warning'
      ? 'bg-amber-100 text-amber-800 border border-amber-300'
      : variant === 'danger'
        ? 'bg-rose-100 text-rose-800 border border-rose-300'
        : variant === 'submitted-no-marks'
          ? 'bg-blue-100 text-blue-700 border border-blue-300'
          : 'bg-slate-100 text-slate-700 border border-slate-200';

  const indicatorClass = indicator === '1'
    ? 'bg-indigo-600 text-white'
    : indicator === '2'
      ? 'bg-amber-500 text-white'
      : 'bg-rose-600 text-white';

  return { displayValue, variant, indicator, badgeClass, indicatorClass, label };
}

function getStudentSubjectCell(student, subjectId, showSheetMarks) {
  const match = (student.batchSubjects || []).find((bs) => bs.subject?.subjectId === subjectId);
  const submission = match?.topupAssignmentSubmission || null;
  const display = getMarksDisplayWithAttempt(submission, showSheetMarks);
  return {
    ...display,
    title: `${match?.subject?.shortName || ''} • ${submission?.submission_status || 'not submitted'}`.trim(),
  };
}

function getStudentStats(student) {
  const batchSubjects = student?.batchSubjects || [];
  let submitted = 0;
  let total = batchSubjects.length;
  let pCount = 0;
  let rCount = 0;
  let snCount = 0;
  let nsCount = 0;

  batchSubjects.forEach((bs) => {
    const submission = bs.topupAssignmentSubmission || {};
    const status = submission.submission_status;
    const marks = submission.marks;
    const second = submission.second_marks;

    if (status && status !== 'not submitted') submitted += 1;

    const hasMarks = marks !== null && marks !== undefined && !Number.isNaN(marks);
    const hasSecond = second !== null && second !== undefined && !Number.isNaN(second);
    const pass = (hasMarks && marks >= 40) || (hasSecond && second >= 40);

    if (!hasMarks && !hasSecond) {
      if (status && status !== 'not submitted') snCount += 1;
      else nsCount += 1;
    } else if (pass) {
      pCount += 1;
    } else {
      rCount += 1;
    }
  });

  const submissionPercentage = total > 0 ? Math.round((submitted / total) * 100) : 0;
  const attendance = student?.attendanceStats?.attendancePercentage;

  return { total, submitted, submissionPercentage, pCount, rCount, snCount, nsCount, attendance };
}

function compareStudentsByMarks(sa, sb, a, b) {
  const descKeys = ['pCount', 'submissionPercentage', 'attendance'];
  for (const key of descKeys) {
    const av = sa[key] ?? -1;
    const bv = sb[key] ?? -1;
    if (av !== bv) return bv - av;
  }

  const ascKeys = ['rCount', 'nsCount', 'snCount'];
  for (const key of ascKeys) {
    const av = sa[key] ?? Number.MAX_SAFE_INTEGER;
    const bv = sb[key] ?? Number.MAX_SAFE_INTEGER;
    if (av !== bv) return av - bv;
  }

  const nameA = (a.fullName || a.student_id || '').toLowerCase();
  const nameB = (b.fullName || b.student_id || '').toLowerCase();
  return nameA.localeCompare(nameB);
}

function getAttendanceBadge(att) {
  if (att === undefined || att === null || Number.isNaN(att)) {
    return 'bg-slate-100 text-slate-600 border border-slate-200';
  }
  if (att >= 80) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (att >= 60) return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-rose-50 text-rose-700 border border-rose-200';
}

function buildStudentImageSrc(imagePath) {
  if (!imagePath) return PLACEHOLDER_IMAGE;
  const trimmed = imagePath.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const baseEndsWithApi = getImageURL('').endsWith('/api');
  const cleaned = baseEndsWithApi && normalized.startsWith('/api/')
    ? normalized.slice(4)
    : normalized;
  return getImageURL(cleaned);
}

function groupStudentsByCourse(students) {
  const groups = new Map();
  students.forEach((s) => {
    const title = s.batchDetail?.courseName || s.batchDetail?.batchName || 'Unknown course';
    const key = title.toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, { title, students: [] });
    }
    groups.get(key).students.push(s);
  });
  return Array.from(groups.values());
}

function ManageExamBoardModal({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState(() => ({
    examBoardName: initial?.examBoardName || '',
    date: initial?.date ? initial.date.slice(0, 10) : '',
    status: initial?.status || 'current',
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      examBoardName: initial?.examBoardName || '',
      date: initial?.date ? initial.date.slice(0, 10) : '',
      status: initial?.status || 'current',
    });
  }, [initial]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.examBoardName.trim()) {
      Swal.fire('Required', 'Exam board name is required', 'warning');
      return;
    }
    if (!form.date) {
      Swal.fire('Required', 'Date is required', 'warning');
      return;
    }

    try {
      setSaving(true);
      await onSave({
        ...form,
        examBoardId: initial?.examBoardId || null,
      });
      onClose();
    } catch (err) {
      // handled upstream
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {initial ? 'Edit Exam Board' : 'New Exam Board'}
            </h2>
            <p className="text-sm text-slate-600">Create or update Topup exam boards.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Exam Board Name</label>
            <input
              name="examBoardName"
              value={form.examBoardName}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="2026 Feb graduation"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Date</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                <option value="current">Current</option>
                <option value="future">Future</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${saving ? 'bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TopupExamBoard() {
  const [examBoards, setExamBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [editModal, setEditModal] = useState({ open: false, student: null, subject: null, batchSubject: null, submission: null });
  const [editStatus, setEditStatus] = useState('not submitted');
  const [editMarks, setEditMarks] = useState('');
  const [editSecondMarks, setEditSecondMarks] = useState('');
  const [editPercentage, setEditPercentage] = useState('');
  const [savingSubmission, setSavingSubmission] = useState(false);
  const [showSheetMarks, setShowSheetMarks] = useState(false);
  const [outsideResponsibility, setOutsideResponsibility] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docLmuId, setDocLmuId] = useState('');
  const [docSubjectId, setDocSubjectId] = useState(DOC_SUBJECTS[0].id);
  const [docCourseworkId, setDocCourseworkId] = useState(DOC_SUBJECTS[0].coursework[0].id);
  const [docCopiedTemplates, setDocCopiedTemplates] = useState([]);

  const docSubject = useMemo(() => DOC_SUBJECTS.find((subj) => subj.id === docSubjectId) || DOC_SUBJECTS[0], [docSubjectId]);
  const docCoursework = useMemo(
    () => docSubject.coursework.find((cw) => cw.id === docCourseworkId) || docSubject.coursework[0],
    [docSubject, docCourseworkId]
  );
  const docTemplates = docCoursework?.templates || [];

  const user = AuthService.getUser();
  const staffType = user?.superAdmin?.staff?.type || user?.type || '';
  const isAdmin = staffType.toLowerCase() === 'admin';

  const isInSelectedBoard = (stu) => {
    if (!selectedBoard) return false;
    const sid = stu?.studentId || stu?.student_id || stu?.studentId;
    return students.some((s) => (s.student_id || s.studentId) === sid);
  };

  const findBoardStudent = (stu) => {
    const sid = stu?.studentId || stu?.student_id || stu?.studentId;
    if (!sid) return null;
    return students.find((s) => (s.student_id || s.studentId) === sid) || null;
  };

  const filteredBoards = useMemo(() => {
    if (showAllStatuses) return examBoards;
    return examBoards.filter((b) => b.status === 'current');
  }, [examBoards, showAllStatuses]);

  const options = useMemo(() => buildOptions(filteredBoards), [filteredBoards]);

  const grouped = useMemo(() => {
    if (!students.length) return [];
    const filtered = searchTerm.trim()
      ? students.filter((s) => {
          const q = searchTerm.toLowerCase();
          return (
            (s.fullName || '').toLowerCase().includes(q) ||
            (s.student_id || '').toLowerCase().includes(q) ||
            (s.student_registration_number || '').toLowerCase().includes(q) ||
            (s.batchDetail?.batchName || '').toLowerCase().includes(q) ||
            (s.batchDetail?.courseName || '').toLowerCase().includes(q)
          );
        })
      : students;
    return groupStudentsByCourse(filtered);
  }, [students, searchTerm]);

  const loadBoards = async () => {
    try {
      setIsLoadingBoards(true);
      const { data } = await axiosInstance.get(API_BASE);
      const boards = data.examBoards || data.data || [];
      setExamBoards(boards);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to load exam boards', 'error');
    } finally {
      setIsLoadingBoards(false);
    }
  };

  const handleRefresh = async () => {
    await loadBoards();
    if (selectedBoard?.value) {
      await loadStudents(selectedBoard.value);
    }
  };

  const loadStudents = async (boardId) => {
    if (!boardId) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'warning');
      return;
    }
    try {
      setIsLoadingStudents(true);
      setOutsideResponsibility(false);
      const { data } = await axiosInstance.get(`${API_BASE}/${boardId}/students`);
      const payload = data.students || data.data || [];
      setStudents(payload);

      const originalCount = typeof data.originalCount === 'number' ? data.originalCount : null;
      const outside = Boolean(data.isOutsideResponsibility);
      const isNotResponsible = outside || (originalCount !== null && originalCount > 0 && payload.length === 0);
      if (isNotResponsible) {
        setOutsideResponsibility(true);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to fetch students', 'error');
    } finally {
      setIsLoadingStudents(false);
    }
  };

  const handleSaveBoard = async (payload) => {
    const isEdit = Boolean(payload.examBoardId);
    try {
      const request = isEdit
        ? axiosInstance.put(`${API_BASE}/${payload.examBoardId}`, payload)
        : axiosInstance.post(API_BASE, payload);
      const { data } = await request;
      const saved = data.examBoard || data.data || payload;
      if (isEdit) {
        setExamBoards((prev) => prev.map((b) => (b.examBoardId === saved.examBoardId ? saved : b)));
      } else {
        setExamBoards((prev) => [saved, ...prev]);
      }
      Swal.fire('Success', isEdit ? 'Exam board updated' : 'Exam board created', 'success');
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || 'Unable to save exam board';
      Swal.fire('Error', message, 'error');
      throw err;
    }
  };

  const handleDeleteBoard = async (board) => {
    const result = await Swal.fire({
      title: 'Delete exam board?',
      text: `"${board.examBoardName}" will be removed permanently.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`${API_BASE}/${board.examBoardId}`);
      setExamBoards((prev) => prev.filter((b) => b.examBoardId !== board.examBoardId));
      if (selectedBoard?.value === board.examBoardId) {
        setSelectedBoard(null);
        setStudents([]);
      }
      Swal.fire('Deleted', 'Exam board removed', 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to delete exam board', 'error');
    }
  };

  const handleRemoveStudent = async (student) => {
    if (!selectedBoard?.value || !student?.id) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'warning');
      return;
    }

    const name = student.fullName || student.student_id || 'this student';
    const result = await Swal.fire({
      title: 'Remove student?',
      text: `Remove ${name} from this exam board?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Remove',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`${API_BASE}/${selectedBoard.value}/students/${student.id}`);
      setStudents((prev) => prev.filter((s) => s.id !== student.id));
      Swal.fire('Removed', `${name} removed from exam board`, 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to remove student', 'error');
    }
  };

  const handleAddStudentToBoard = async (stu) => {
    if (!selectedBoard?.value) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'warning');
      return;
    }
    const studentId = stu?.studentId || stu?.student_id;
    const registrationNumber = stu?.registrationNumber || stu?.student_registration_number || null;
    if (!studentId) {
      Swal.fire('Missing data', 'Student ID is missing; cannot add.', 'warning');
      return;
    }

    try {
      await axiosInstance.post(`${API_BASE}/${selectedBoard.value}/students`, {
        studentId,
        registrationNumber,
        student_id: studentId,
        student_registration_number: registrationNumber,
      });
      await loadStudents(selectedBoard.value);
      Swal.fire('Added', `${stu.fullName || studentId} added to exam board`, 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to add student', 'error');
    }
  };

  const openDocNamesModal = () => {
    if (!selectedBoard?.value) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'info');
      return;
    }
    setDocModalOpen(true);
    setDocCopiedTemplates([]);
  };

  const closeDocNamesModal = () => {
    setDocModalOpen(false);
    setDocCopiedTemplates([]);
  };

  const handleDocSubjectSelect = (subjectId) => {
    setDocSubjectId(subjectId);
    setDocCopiedTemplates([]);
  };

  const handleDocCourseworkSelect = (courseworkId) => {
    setDocCourseworkId(courseworkId);
    setDocCopiedTemplates([]);
  };

  const resetDocBuilder = () => {
    setDocCopiedTemplates([]);
  };

  const buildDocName = (template) => {
    const trimmed = docLmuId.trim();
    return trimmed ? `${trimmed}${template}` : template;
  };

  const handleCopyDocName = async (template) => {
    const text = buildDocName(template);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setDocCopiedTemplates((prev) => (prev.includes(template) ? prev : [...prev, template]));
    } catch (err) {
      console.error(err);
      Swal.fire('Copy failed', 'Unable to copy document name.', 'error');
    }
  };

  const openSubmissionModal = (student, subject) => {
    const batchSubject = (student.batchSubjects || []).find((bs) => bs.subject?.subjectId === subject.subjectId);
    if (!batchSubject) return;
    const submission = batchSubject.topupAssignmentSubmission || null;
    setEditModal({ open: true, student, subject, batchSubject, submission });
    setEditStatus(submission?.submission_status || 'not submitted');
    setEditMarks(submission?.marks ?? '');
    setEditSecondMarks(submission?.second_marks ?? '');
    setEditPercentage(submission?.percentage_rate ?? '');
  };

  const closeSubmissionModal = () => {
    setEditModal({ open: false, student: null, subject: null, batchSubject: null, submission: null });
    setEditStatus('not submitted');
    setEditMarks('');
    setEditSecondMarks('');
    setEditPercentage('');
    setSavingSubmission(false);
  };

  const handleSaveSubmission = async () => {
    if (!selectedBoard?.value || !editModal.open || !editModal.student || !editModal.batchSubject) {
      Swal.fire('Missing data', 'Select an exam board and a valid subject to edit.', 'warning');
      return;
    }

    const studentId = editModal.student.student_id || editModal.student.studentId;
    const batchSubjectId = editModal.batchSubject.batchSubjectId;
    if (!studentId || !batchSubjectId) {
      Swal.fire('Missing data', 'Student or subject is missing; cannot save.', 'warning');
      return;
    }

    try {
      setSavingSubmission(true);
      const payload = {
        student_id: studentId,
        batch_subject_id: batchSubjectId,
        submission_status: editStatus,
        marks: editMarks === '' ? null : Number(editMarks),
        second_marks: editSecondMarks === '' ? null : Number(editSecondMarks),
        percentage_rate: editPercentage === '' ? null : Number(editPercentage),
      };
      const { data } = await axiosInstance.put(`${API_BASE}/${selectedBoard.value}/submissions`, payload);
      const saved = data?.submission || {};
      const updatedSubmission = {
        ...(editModal.submission || {}),
        id: saved.id || editModal.submission?.id,
        submission_status: saved.submission_status ?? editStatus,
        marks: saved.marks ?? (editMarks === '' ? null : Number(editMarks)),
        second_marks: saved.second_marks ?? (editSecondMarks === '' ? null : Number(editSecondMarks)),
        percentage_rate: saved.percentage_rate ?? (editPercentage === '' ? null : Number(editPercentage)),
        batch_subjectId: saved.batch_subjectId || batchSubjectId,
        studentId: saved.studentId || studentId,
        updatedAt: saved.updatedAt || new Date().toISOString(),
      };

      setStudents((prev) => prev.map((s) => {
        const matchById = s.id && editModal.student.id && s.id === editModal.student.id;
        const matchByStudent = s.student_id && editModal.student.student_id && s.student_id === editModal.student.student_id;
        if (!matchById && !matchByStudent) return s;
        const updatedBatchSubjects = (s.batchSubjects || []).map((bs) =>
          bs.batchSubjectId === batchSubjectId
            ? { ...bs, topupAssignmentSubmission: updatedSubmission }
            : bs
        );
        return { ...s, batchSubjects: updatedBatchSubjects };
      }));

      Swal.fire('Saved', 'Assignment submission updated.', 'success');
      closeSubmissionModal();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to save submission', 'error');
    } finally {
      setSavingSubmission(false);
    }
  };

  const executeStudentSearch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchError('Type at least 2 characters to search');
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError('');
      const { data } = await axiosInstance.get(`${API_BASE}/search-students`, { params: { q } });
      setSearchResults(data.students || data.data || []);
    } catch (err) {
      console.error(err);
      setSearchError(err.response?.data?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    loadBoards();
  }, []);

  useEffect(() => {
    if (!docSubject.coursework.find((cw) => cw.id === docCourseworkId)) {
      setDocCourseworkId(docSubject.coursework[0]?.id || '');
    }
  }, [docSubject, docCourseworkId]);

  return (
    <DashboardLayout title="Topup Exam Board">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Topup Exam Board</h1>
            <p className="text-slate-600 text-sm">Select a board and review subject-wise outcomes.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Show Sheet Marks</span>
              <button
                type="button"
                onClick={() => setShowSheetMarks(!showSheetMarks)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  showSheetMarks ? 'bg-indigo-600' : 'bg-slate-200'
                }`}
                title="Cap second-attempt marks display to 43"
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    showSheetMarks ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {isAdmin && selectedBoard && (
              <button
                type="button"
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-100"
                title="Add students"
                onClick={() => {
                  setSearchDrawerOpen(true);
                  setSearchQuery('');
                  setSearchResults([]);
                  setSearchError('');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                  <circle cx="9" cy="7" r="4" />
                  <path d="M5 21v-2a4 4 0 0 1 4-4h0a4 4 0 0 1 4 4v2" />
                  <path d="M17 11v6" />
                  <path d="M20 14h-6" />
                </svg>
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setModalOpen(true)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Manage Exam Boards
              </button>
            )}
            <button
              onClick={handleRefresh}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="rounded-xl bg-white shadow-sm border border-slate-200 p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full max-w-lg">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Select exam board</label>
              <Select
                value={selectedBoard}
                onChange={(option) => {
                  setSelectedBoard(option);
                  setStudents([]);
                  setOutsideResponsibility(false);
                }}
                options={options}
                isLoading={isLoadingBoards}
                placeholder="2026 Feb graduation"
                classNamePrefix="exam-board-select"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showAllStatuses}
                onChange={(e) => setShowAllStatuses(e.target.checked)}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show all exam boards
            </label>
            <button
              onClick={() => loadStudents(selectedBoard?.value)}
              disabled={!selectedBoard || isLoadingStudents}
              className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                !selectedBoard || isLoadingStudents
                  ? 'bg-indigo-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {isLoadingStudents ? 'Loading...' : 'Show Students'}
            </button>
          </div>

          {isAdmin && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredBoards.map((board) => (
                <div
                  key={board.examBoardId}
                  className={`rounded-lg border border-slate-200 p-4 shadow-sm ${
                    selectedBoard?.value === board.examBoardId ? 'ring-2 ring-indigo-200' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900 truncate" title={board.examBoardName}>
                      {board.examBoardName}
                    </h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      statusBadges[board.status] || statusBadges.completed
                    }`}>
                      {board.status}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{formatDate(board.date)}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                    <button
                      onClick={() => setSelectedBoard({ value: board.examBoardId, label: board.examBoardName })}
                      className="rounded-lg border border-slate-300 px-3 py-1 text-slate-700 hover:bg-slate-50"
                    >
                      Select
                    </button>
                    <button
                      onClick={() => { setEditingBoard(board); setModalOpen(true); }}
                      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700 hover:bg-amber-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBoard(board)}
                      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-rose-700 hover:bg-rose-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {!filteredBoards.length && (
                <div className="col-span-full rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
                  {isLoadingBoards ? 'Loading exam boards...' : 'No exam boards found'}
                </div>
              )}
            </div>
          )}

          {selectedBoard && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800">
              <div className="font-semibold">Selected Exam Board</div>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span>{selectedBoard.label}</span>
                <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-emerald-700">current</span>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white shadow-sm border border-slate-200 p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Students</h2>
              <p className="text-sm text-slate-600">View subject-wise marks for the selected board.</p>
            </div>
            <div className="flex w-full max-w-md items-center gap-2">
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search in this course..."
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <button
                type="button"
                onClick={openDocNamesModal}
                disabled={!selectedBoard}
                className={`rounded-lg px-3 py-2 text-sm font-semibold shadow-sm border ${
                  selectedBoard
                    ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                    : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                Doc Names
              </button>
            </div>
          </div>

          {!selectedBoard && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              Select an exam board and click "Show Students".
            </div>
          )}

          {selectedBoard && !students.length && !isLoadingStudents && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              {outsideResponsibility
                ? 'This exam board is not under your responsibility.'
                : 'No students loaded yet.'}
            </div>
          )}

          {isLoadingStudents && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              Loading students...
            </div>
          )}

          {!isLoadingStudents && grouped.map((group, groupIdx) => {
            const subjects = getSubjectsForGroup(group.students, group.title);
            const statsCache = new Map();
            const getStats = (student) => {
              if (!statsCache.has(student)) {
                statsCache.set(student, getStudentStats(student));
              }
              return statsCache.get(student);
            };
            const sortedStudents = [...group.students].sort((a, b) => compareStudentsByMarks(getStats(a), getStats(b), a, b));
            return (
              <div key={groupIdx} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between rounded-lg bg-slate-900 px-4 py-3 text-sm text-white shadow-sm">
                  <div className="font-semibold">{group.title}</div>
                  <div className="text-slate-200">Students: {group.students.length}</div>
                </div>

                <div className="overflow-x-auto overflow-y-auto max-h-[70vh] rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="min-w-max text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th
                          className="sticky left-0 z-20 w-12 min-w-[48px] px-3 py-2 text-left font-semibold bg-slate-50"
                        >
                          #
                        </th>
                        <th
                          className="sticky z-20 w-16 min-w-[64px] px-3 py-2 text-left font-semibold bg-slate-50"
                          style={{ left: 48 }}
                        >
                          FUN
                        </th>
                        <th
                          className="sticky z-20 w-64 min-w-[256px] px-3 py-2 text-left font-semibold bg-slate-50"
                          style={{ left: 112 }}
                        >
                          Identity
                        </th>
                        {subjects.map((subj) => (
                          <th key={subj.subjectId} className="px-2 py-2 text-center font-semibold" title={subj.fullName}>
                            {subj.shortName || subj.fullName || 'Subj'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sortedStudents.map((student, idx) => {
                        const stats = getStats(student);
                        const img = buildStudentImageSrc(student.imagePath);
                        const attBadge = getAttendanceBadge(stats.attendance);
                        return (
                          <tr key={student.id || student.student_id || idx} className="hover:bg-slate-50">
                            <td
                              className="sticky left-0 z-10 w-12 min-w-[48px] px-3 py-3 align-top text-xs text-slate-500 bg-white"
                            >
                              {idx + 1}
                            </td>
                            <td
                              className="sticky z-10 w-16 min-w-[64px] px-3 py-3 align-top bg-white"
                              style={{ left: 48 }}
                            >
                              <div className="flex flex-col gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveStudent(student)}
                                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-600 shadow text-white hover:bg-red-700"
                                  title="Remove"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12m-9 0v10m6-10v10M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-9 0h10v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 shadow text-white hover:bg-indigo-700"
                                  title="Archive"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l1.664-2.77A2 2 0 016.422 3h11.156a2 2 0 011.758 1.23L21 7M4 7h16M5 7v10a2 2 0 002 2h10a2 2 0 002-2V7M9 11h6" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td
                              className="sticky z-10 w-64 min-w-[256px] px-3 py-3 align-top bg-white"
                              style={{ left: 112 }}
                            >
                              <div className="flex gap-3">
                                <PassportWrapper>
                                  <PassportImage
                                    src={img}
                                    alt={student.fullName}
                                    loading="lazy"
                                    onError={(e) => {
                                      if (e.currentTarget.src !== PLACEHOLDER_IMAGE) {
                                        e.currentTarget.src = PLACEHOLDER_IMAGE;
                                      }
                                    }}
                                  />
                                </PassportWrapper>
                                <div className="text-xs text-slate-700 space-y-0.5">
                                  <div className="text-sm font-semibold text-slate-900">{student.fullName}</div>
                                  <div className="text-slate-600">ID: {student.student_id || '—'}</div>
                                  <div className="text-slate-600">Reg: {student.student_registration_number || '—'}</div>
                                  <div className="text-slate-600">Tel: {student.telephone || '—'}</div>
                                  <div className="text-[11px] text-slate-500">
                                    {student.batchDetail?.batchName || student.batchDetail?.courseName || 'Batch N/A'}
                                  </div>
                                  <div className="flex flex-wrap gap-1.5 pt-1 text-[11px] font-semibold">
                                    <span className={`rounded px-2 py-1 ${attBadge}`}>Att: {stats.attendance ?? 'N/A'}%</span>
                                    <span className="rounded px-2 py-1 bg-slate-100 text-slate-700 border border-slate-200">Sub: {stats.submissionPercentage}%</span>
                                    <span className="rounded px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200">P:{stats.pCount}</span>
                                    <span className="rounded px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200">SN:{stats.snCount}</span>
                                    <span className="rounded px-2 py-1 bg-slate-100 text-slate-700 border border-slate-200">NS:{stats.nsCount}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            {subjects.map((subj) => {
                              const cell = getStudentSubjectCell(student, subj.subjectId, showSheetMarks);
                              return (
                                <td
                                  key={`${student.student_id}-${subj.subjectId}`}
                                  className="px-2 py-3 text-center align-middle cursor-pointer transition-colors hover:bg-slate-100"
                                  onDoubleClick={() => openSubmissionModal(student, subj)}
                                >
                                  <span className="relative inline-flex items-center">
                                    <span
                                      title={cell.title}
                                      className={`inline-flex h-8 w-12 items-center justify-center rounded ${cell.badgeClass} text-xs font-semibold`}
                                    >
                                      {cell.variant === 'not-submitted' ? 'NS' : cell.variant === 'submitted-no-marks' ? 'SN' : (cell.label || '-')}
                                    </span>
                                    {cell.indicator && cell.variant !== 'not-submitted' && cell.variant !== 'submitted-no-marks' && (
                                      <span
                                        className={`-ml-1 inline-flex items-center justify-center h-4 w-4 text-[10px] font-bold rounded-full ${cell.indicatorClass} border border-white shadow`}
                                        title={cell.indicator === 'F' ? 'Failed (both attempts < 40)' : (cell.indicator === 'R' ? 'Below pass mark' : (cell.indicator === '1' ? 'First attempt' : 'Second attempt'))}
                                        style={{ transform: 'translateY(-6px)' }}
                                      >
                                        {cell.indicator}
                                      </span>
                                    )}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editModal.open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-10">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div className="text-lg font-semibold text-slate-900">Edit Assignment Submission</div>
              <button
                type="button"
                className="text-slate-500 hover:text-slate-700"
                onClick={closeSubmissionModal}
              >
                Close
              </button>
            </div>

            <div className="space-y-4 px-6 py-4">
              <div className="rounded-lg bg-indigo-50 px-4 py-3 text-sm text-slate-800 border border-indigo-100">
                <div className="font-semibold text-indigo-800 mb-1">Student Information</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <div>Name: {editModal.student?.fullName || '—'}</div>
                  <div>Student ID: {editModal.student?.student_id || editModal.student?.studentId || '—'}</div>
                  <div>Registration No: {editModal.student?.student_registration_number || '—'}</div>
                  <div>Contact: {editModal.student?.telephone || '—'}</div>
                </div>
              </div>

              <div className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-slate-800 border border-emerald-100">
                <div className="font-semibold text-emerald-800 mb-1">Subject Information</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <div>Subject: {editModal.subject?.fullName || '—'}</div>
                  <div>Short Name: {editModal.subject?.shortName || '—'}</div>
                  <div>Semester: {editModal.subject?.semester || '—'}</div>
                  <div>Staff: {editModal.batchSubject?.staff?.fullName || '—'}</div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-800">
                  Submission Status <span className="text-rose-500">*</span>
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {submissionStatusOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800">First Attempt Marks</label>
                    <input
                      type="number"
                      value={editMarks}
                      onChange={(e) => setEditMarks(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="e.g. 55"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800">Second Attempt Marks</label>
                    <input
                      type="number"
                      value={editSecondMarks}
                      onChange={(e) => setEditSecondMarks(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="e.g. 43"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800">Percentage Rate</label>
                    <input
                      type="number"
                      value={editPercentage}
                      onChange={(e) => setEditPercentage(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="optional"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">Leave blank to set values to null.</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 bg-slate-50">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white"
                onClick={closeSubmissionModal}
                disabled={savingSubmission}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                onClick={handleSaveSubmission}
                disabled={savingSubmission}
              >
                {savingSubmission ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {docModalOpen && (
        <div className="fixed inset-0 z-[55] flex items-start justify-center bg-black/50 px-4 py-10">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-slate-900 px-6 py-4 text-white">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-300">Document Toolkit</p>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">Doc Name Builder</span>
                  {selectedBoard?.label && (
                    <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
                      {selectedBoard.label}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-300">Craft consistent filenames before uploading evidence to the board.</p>
              </div>
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-white/30 px-3 py-1 text-sm font-semibold text-white hover:bg-white/10"
                onClick={closeDocNamesModal}
              >
                <span>Close</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6 px-6 py-6">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <span className="font-semibold text-slate-900">Tip:</span> Use the same LMU ID and topic each time so the moderation folders stay searchable.
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.08)] space-y-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">1</span>
                    Step 1 · Enter LMU ID
                  </div>
                  <div className="space-y-1">
                    <input
                      value={docLmuId}
                      onChange={(e) => setDocLmuId(e.target.value)}
                      placeholder="LMU/XX/XXXX"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                    <p className="text-xs text-slate-500">Optional, but adding it now saves you from renaming files later.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">2</span>
                    Step 2 · Select Subject
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {DOC_SUBJECTS.map((subject) => (
                      <button
                        key={subject.id}
                        type="button"
                        onClick={() => handleDocSubjectSelect(subject.id)}
                        className={`min-w-[72px] rounded-xl border px-4 py-2 text-sm font-semibold text-center transition-colors duration-150 ${
                          docSubjectId === subject.id
                            ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                            : 'border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600'
                        }`}
                      >
                        {subject.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">3</span>
                    Step 3 · Select Coursework
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {docSubject.coursework.map((coursework) => (
                      <button
                        key={`${docSubject.id}-${coursework.id}`}
                        type="button"
                        onClick={() => handleDocCourseworkSelect(coursework.id)}
                        className={`min-w-[72px] rounded-xl border px-4 py-2 text-sm font-semibold text-center transition-colors duration-150 ${
                          docCourseworkId === coursework.id
                            ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                            : 'border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600'
                        }`}
                      >
                        {coursework.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900">Selected Path</div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                      {docSubject.label} &gt; {docCoursework.label}
                    </span>
                    <button
                      type="button"
                      onClick={resetDocBuilder}
                      className="rounded-full border border-slate-300 bg-white p-2 text-slate-500 hover:border-blue-200 hover:text-blue-600"
                      title="Reset inputs"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h4V3" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 17h-4v4" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 11a7 7 0 0112-4l2 2" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 13a7 7 0 01-12 4l-2-2" />
                      </svg>
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-xs text-slate-500">We generate prefixes from this path and append the LMU ID if provided.</p>
              </div>

              <div className="rounded-[22px] border border-slate-200 bg-white">
                <div className="flex items-center justify-between border-b border-slate-100 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <span>Preview</span>
                  <span>Action</span>
                </div>
                {docTemplates.length ? (
                  docTemplates.map((template) => {
                    const docName = buildDocName(template);
                    const copied = docCopiedTemplates.includes(template);
                    return (
                      <div key={template} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 text-sm text-slate-800 hover:bg-slate-50">
                        <span className="font-mono text-base font-medium text-slate-900 break-all">{docName}</span>
                        <button
                          type="button"
                          onClick={() => handleCopyDocName(template)}
                          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
                            copied ? 'bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor" fill="none" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16h8a2 2 0 002-2V6a2 2 0 00-2-2H8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 8h2a2 2 0 012 2v8a2 2 0 01-2 2H10a2 2 0 01-2-2v-2" />
                          </svg>
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-5 py-6 text-center text-sm text-slate-600">
                    No templates configured for this selection.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ManageExamBoardModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingBoard(null); }}
        onSave={handleSaveBoard}
        initial={editingBoard}
      />

      {isAdmin && searchDrawerOpen && (
        <div className="fixed inset-0 z-[1300] flex">
          <div
            className="flex-1 bg-black/40"
            onClick={() => setSearchDrawerOpen(false)}
            role="presentation"
          />
          <div className="w-full max-w-lg bg-white h-full shadow-2xl border-l border-slate-200 transform translate-x-0 transition-transform duration-200 ease-out">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Search Students</div>
                <div className="text-xs text-slate-500">Search by ID, registration, WhatsApp group, batch, or name.</div>
              </div>
              <button
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                onClick={() => setSearchDrawerOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              <form
                onSubmit={(e) => { e.preventDefault(); executeStudentSearch(); }}
                className="flex gap-2"
              >
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search student ID, registration number, WhatsApp group, batch name..."
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
                <button
                  type="submit"
                  disabled={searchLoading}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${searchLoading ? 'bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </form>

              {!searchLoading && searchResults.length > 0 && (() => {
                const added = searchResults.filter((r) => isInSelectedBoard(r)).length;
                const notAdded = searchResults.length - added;
                return (
                  <div className="text-xs font-semibold text-slate-600 flex gap-3">
                    <span>Total: {searchResults.length}</span>
                    <span className="text-emerald-700">Added: {added}</span>
                    <span className="text-slate-700">Not added: {notAdded}</span>
                  </div>
                );
              })()}

              {searchError && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{searchError}</div>
              )}

              {!searchLoading && !searchResults.length && !searchError && (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  Enter a search to see matching students.
                </div>
              )}

              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {searchResults.map((stu) => {
                  const img = buildStudentImageSrc(stu.imagePath);
                  const inBoard = isInSelectedBoard(stu);
                  const boardStudent = findBoardStudent(stu);
                  const stats = boardStudent ? getStudentStats(boardStudent) : (stu.stats || null);
                  const attendanceFromStats = stats ? (stats.attendance ?? stats.attendancePercentage) : null;
                  const attVal = attendanceFromStats !== undefined && attendanceFromStats !== null
                    ? attendanceFromStats
                    : (stu.attendanceStats?.attendancePercentage ?? null);
                  const attBadge = getAttendanceBadge(attVal);
                  return (
                    <div key={stu.student_id || stu.studentId || stu.id}
                      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                      <SmallPassportWrapper>
                        <PassportImage
                          src={img}
                          alt={stu.fullName}
                          loading="lazy"
                          onError={(e) => {
                            if (e.currentTarget.src !== PLACEHOLDER_IMAGE) {
                              e.currentTarget.src = PLACEHOLDER_IMAGE;
                            }
                          }}
                        />
                      </SmallPassportWrapper>
                      <div className="flex-1 text-sm text-slate-700 space-y-0.5">
                        <div className="font-semibold text-slate-900">{stu.fullName}</div>
                        <div className="text-slate-600">ID: {stu.student_id || stu.studentId || '—'}</div>
                        <div className="text-slate-600">Reg: {stu.student_registration_number || stu.registrationNumber || '—'}</div>
                        <div className="text-slate-600">
                          Batch: {stu.batchName || stu.batchInternalName || boardStudent?.batchDetail?.batchName || boardStudent?.batchDetail?.courseName || '—'}
                        </div>
                        <div className="text-slate-600">
                          Course: {boardStudent?.batchDetail?.courseName || boardStudent?.batchDetail?.courseShortName || stu.courseName || stu.courseShortName || '—'}
                        </div>
                        <div className="flex flex-wrap gap-1.5 pt-1 text-[11px] font-semibold">
                          <span className={`rounded px-2 py-1 ${attBadge}`}>Att: {attVal ?? 'N/A'}%</span>
                          {stats && (
                            <span className="rounded px-2 py-1 bg-slate-100 text-slate-700 border border-slate-200">Sub: {stats.submissionPercentage}%</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {inBoard && (
                          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-200">Added</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleAddStudentToBoard(stu)}
                          disabled={!selectedBoard || inBoard}
                          className={`rounded-lg px-3 py-2 text-xs font-semibold text-white ${
                            !selectedBoard || inBoard ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                          }`}
                        >
                          Add to board
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
